import { initializeApp } from 'firebase/app'
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore'
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions'
import { buildTime } from '#event-config'

/**
 * One Firebase app for the whole SPA. Config comes from Vite env vars because the
 * web API key is per-project and is not a secret — App Check is what makes the
 * callables unusable from anywhere but this origin.
 */
const config = {
  apiKey: import.meta.env['VITE_FIREBASE_API_KEY'] ?? 'demo-api-key',
  authDomain: import.meta.env['VITE_FIREBASE_AUTH_DOMAIN'] ?? `${buildTime.projectId}.firebaseapp.com`,
  projectId: import.meta.env['VITE_FIREBASE_PROJECT_ID'] ?? buildTime.projectId,
  /*
   * Required for product-image uploads. Without it the Storage SDK throws
   * `storage/no-default-bucket` at the moment an organiser picks a photo — which
   * looks like a broken upload button rather than a missing config line.
   *
   * Projects created from 2024 on get `<project>.firebasestorage.app`; older ones
   * use `<project>.appspot.com`, hence the override.
   */
  storageBucket:
    import.meta.env['VITE_FIREBASE_STORAGE_BUCKET'] ?? `${buildTime.projectId}.firebasestorage.app`,
  appId: import.meta.env['VITE_FIREBASE_APP_ID'] ?? 'demo-app-id',
}

export const app = initializeApp(config)
export const db = getFirestore(app)
export const functions = getFunctions(app, buildTime.region)

/*
 * Auth is deliberately NOT created here. Only `/admin` signs anybody in, and
 * `firebase/auth` is ~35 kB gzipped — importing it from the module every page
 * touches would put the whole sign-in SDK in the buyer's entry bundle. It is
 * created inside the lazy admin chunk instead (`pages/admin/auth.ts`).
 */

export const useEmulators = import.meta.env.DEV && import.meta.env['VITE_USE_EMULATORS'] !== 'false'

/**
 * Resolves once App Check is initialised (immediately when it is not configured).
 * Awaited before any callable, so the first `createOrder` cannot race the loader
 * and fail with an opaque "missing App Check token".
 */
export let appCheckReady: Promise<void> = Promise.resolve()

if (useEmulators) {
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
} else {
  const siteKey = import.meta.env['VITE_RECAPTCHA_SITE_KEY']
  /*
   * Only wired up when a key exists. Calling initializeAppCheck with a missing key
   * fails every callable with an opaque error, which reads as "the site is broken"
   * rather than "App Check is misconfigured".
   *
   * Imported dynamically because App Check plus the reCAPTCHA loader is ~15 kB the
   * landing page never needs — it reads a public Firestore document and calls
   * nothing. The token is only required once the buyer reaches `createOrder`, and
   * `appCheckReady` is what the API client waits on.
   */
  if (siteKey) {
    appCheckReady = import('firebase/app-check').then(
      ({ initializeAppCheck, ReCaptchaEnterpriseProvider }) => {
        initializeAppCheck(app, {
          provider: new ReCaptchaEnterpriseProvider(siteKey),
          isTokenAutoRefreshEnabled: true,
        })
      },
    )
  } else if (import.meta.env.PROD) {
    console.warn('App Check is not configured: VITE_RECAPTCHA_SITE_KEY is missing')
  }
}
